// Scheduled Drug Storage & Compliance — data model.
// Australian national Poisons Standard (SUSMP), kept state-generic. Schedules verified
// against SUSMP (Feb 2026). Storage rules summarised at vet-nurse standard — always defer
// to your state's Drugs & Poisons regulations and clinic protocols.

// Generic, unbranded container photographs (blank labels — drug name overlaid in the UI).
const IMG = {
  vial: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/77ec5520706a5d3c3951bcc409b4e47305be21c88317dee443168a8cee5a6bac.png",
  ampoule: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/6b3d2638bac82d97664f47eca77eba6c310df24928a980259f346006d0314972.png",
  tablets: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/055a8314ac3048ce102cc54d50cec96246f3f7bbe005552e6a8288c125535c2a.png",
  box: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/c9f5cba873ad4eee33b1af41d41e88b78d0003063d1d002044a49552fc0b945b.png",
  dropper: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/40fd701e718660eb53ba28dc5a7210bab31bf66b01438c49b8fc4d41ef9ab2bb.png",
  tub: "https://static.prod-images.emergentagent.com/jobs/71abba4a-702d-4e02-b451-e9e11020a1be/images/e16dc313e87da92796a6a98cd2fce6f17f31b96b8c62d6a3d175dd3dd86c45ba.png",
};

// Storage zones (3-way mapping used in the "store it" phase).
export const ZONES = [
  {
    id: "general",
    label: "Open dispensary shelf",
    sub: "Unscheduled / general sale",
    accent: "#0f766e",
    desc: "General, unscheduled stock — no special security required beyond normal stockroom access.",
  },
  {
    id: "cabinet",
    label: "Locked S4 cabinet",
    sub: "Prescription Only (S4)",
    accent: "#0047AB",
    desc: "S4 medicines must be stored so the public cannot access them — a locked cabinet or a room/area not accessible to clients.",
  },
  {
    id: "safe",
    label: "S8 controlled-drug safe",
    sub: "Controlled Drugs (S8)",
    accent: "#b45309",
    desc: "S8 drugs must be kept in a securely fixed (bolted) locked safe of approved construction, with restricted key access and a current S8 register.",
  },
];

// Schedule classification options offered in the "identify" phase.
export const SCHEDULE_OPTIONS = [
  { id: "unscheduled", label: "Unscheduled", hint: "General sale" },
  { id: "S3", label: "S3", hint: "Pharmacist Only" },
  { id: "S4", label: "S4", hint: "Prescription Only" },
  { id: "S8", label: "S8", hint: "Controlled Drug" },
];

// Which zone a given schedule belongs in.
export const SCHEDULE_ZONE = { unscheduled: "general", S3: "general", S4: "cabinet", S8: "safe" };

export const SCHEDULED_DRUGS = [
  // ── Unscheduled — open shelf ───────────────────────────────────────────────
  { id: "earclean", name: "Ear cleaning solution", active: "Cleaning agents", strength: "120 mL", form: "dropper", img: IMG.dropper,
    schedule: "unscheduled", note: "A cleaning/grooming product with no scheduled active — general sale, stored on the open dispensary shelf." },
  { id: "jointchews", name: "Joint care chews", active: "Glucosamine / chondroitin", strength: "60 chews", form: "tub", img: IMG.tub,
    schedule: "unscheduled", note: "A nutraceutical supplement — unscheduled, no storage security required." },
  { id: "skinwash", name: "Antiseptic skin wash", active: "Chlorhexidine 2%", strength: "200 mL", form: "box", img: IMG.box,
    schedule: "unscheduled", note: "Low-concentration topical antiseptic — unscheduled general sale." },

  // ── S4 — locked cabinet ────────────────────────────────────────────────────
  { id: "meloxicam", name: "Meloxicam oral suspension", active: "Meloxicam 1.5 mg/mL", strength: "32 mL", form: "dropper", img: IMG.dropper,
    schedule: "S4", note: "NSAID — Prescription Only (S4). Store in the locked S4 cabinet." },
  { id: "carprofen", name: "Carprofen chewable tablets", active: "Carprofen 50 mg", strength: "60 tabs", form: "tablets", img: IMG.tablets,
    schedule: "S4", note: "NSAID — Prescription Only (S4)." },
  { id: "amoxiclav", name: "Amoxicillin–clavulanate tablets", active: "250 mg", strength: "100 tabs", form: "box", img: IMG.box,
    schedule: "S4", note: "Antibiotic — Prescription Only (S4)." },
  { id: "diazepam", name: "Diazepam injection", active: "Diazepam 5 mg/mL", strength: "2 mL ampoule", form: "ampoule", img: IMG.ampoule,
    schedule: "S4", trap: true,
    note: "TRAP: most benzodiazepines are S4 in Australia (only alprazolam & flunitrazepam are S8). Diazepam is S4 — but as a benzodiazepine it is a drug of dependence with extra record-keeping (S4D / Appendix D) in several states. Locked S4 cabinet." },
  { id: "tramadol", name: "Tramadol capsules", active: "Tramadol 50 mg", strength: "20 caps", form: "tablets", img: IMG.tablets,
    schedule: "S4", trap: true,
    note: "TRAP: although tramadol is opioid-like, in Australia it is Schedule 4 (Prescription Only), NOT S8. Locked S4 cabinet." },
  { id: "propofol", name: "Propofol injection", active: "Propofol 10 mg/mL", strength: "20 mL vial", form: "vial", img: IMG.vial,
    schedule: "S4", note: "Induction agent — Prescription Only (S4). Locked S4 cabinet." },
  { id: "ace", name: "Acepromazine injection", active: "ACP 10 mg/mL", strength: "10 mL vial", form: "vial", img: IMG.vial,
    schedule: "S4", note: "Phenothiazine sedative — Prescription Only (S4)." },

  // ── S8 — controlled-drug safe + register ───────────────────────────────────
  { id: "methadone", name: "Methadone injection", active: "Methadone 10 mg/mL", strength: "10 mL vial", form: "vial", img: IMG.vial,
    schedule: "S8", register: true, note: "Opioid — Controlled Drug (S8). Locked S8 safe + S8 register entry for every transaction." },
  { id: "morphine", name: "Morphine injection", active: "Morphine 10 mg/mL", strength: "1 mL ampoule", form: "ampoule", img: IMG.ampoule,
    schedule: "S8", register: true, note: "Opioid — Controlled Drug (S8). S8 safe + register." },
  { id: "fentanyl", name: "Fentanyl injection", active: "Fentanyl 50 µg/mL", strength: "2 mL ampoule", form: "ampoule", img: IMG.ampoule,
    schedule: "S8", register: true, note: "Potent opioid — Controlled Drug (S8). S8 safe + register." },
  { id: "ketamine", name: "Ketamine injection", active: "Ketamine 100 mg/mL", strength: "10 mL vial", form: "vial", img: IMG.vial,
    schedule: "S8", register: true, trap: true,
    note: "TRAP: ketamine was rescheduled to Schedule 8 in Australia — it must be kept in the S8 safe with full register entries, even though it's often (incorrectly) thought of as S4." },
  { id: "buprenorphine", name: "Buprenorphine injection", active: "Buprenorphine 0.3 mg/mL", strength: "10 mL vial", form: "vial", img: IMG.vial,
    schedule: "S8", register: true, note: "Opioid — Controlled Drug (S8). S8 safe + register." },
  { id: "pethidine", name: "Pethidine injection", active: "Pethidine 50 mg/mL", strength: "2 mL ampoule", form: "ampoule", img: IMG.ampoule,
    schedule: "S8", register: true, note: "Opioid — Controlled Drug (S8). S8 safe + register." },
  { id: "hydromorphone", name: "Hydromorphone injection", active: "Hydromorphone 2 mg/mL", strength: "1 mL ampoule", form: "ampoule", img: IMG.ampoule,
    schedule: "S8", register: true, note: "Potent opioid — Controlled Drug (S8). S8 safe + register." },

  // ── more unscheduled — open shelf ──────────────────────────────────────────
  { id: "electrolyte", name: "Electrolyte rehydration powder", active: "Glucose / electrolytes", strength: "100 g", form: "box", img: IMG.box,
    schedule: "unscheduled", note: "Oral rehydration supplement — unscheduled general sale, stored on the open shelf." },
  { id: "probiotic", name: "Probiotic gut paste", active: "Live cultures", strength: "15 mL", form: "tub", img: IMG.tub,
    schedule: "unscheduled", note: "Nutraceutical probiotic — unscheduled, no storage security required." },

  // ── more S4 — locked cabinet ───────────────────────────────────────────────
  { id: "gabapentin", name: "Gabapentin capsules", active: "Gabapentin 100 mg", strength: "100 caps", form: "tablets", img: IMG.tablets,
    schedule: "S4", note: "Analgesic / anticonvulsant — Prescription Only (S4). Locked S4 cabinet." },
  { id: "trazodone", name: "Trazodone tablets", active: "Trazodone 100 mg", strength: "30 tabs", form: "tablets", img: IMG.tablets,
    schedule: "S4", note: "Behavioural anxiolytic — Prescription Only (S4)." },
  { id: "maropitant", name: "Maropitant injection", active: "Maropitant 10 mg/mL", strength: "20 mL vial", form: "vial", img: IMG.vial,
    schedule: "S4", note: "Antiemetic — Prescription Only (S4). Locked S4 cabinet." },
  { id: "dexmed", name: "Dexmedetomidine injection", active: "Dexmedetomidine 0.5 mg/mL", strength: "10 mL vial", form: "vial", img: IMG.vial,
    schedule: "S4", note: "Alpha-2 sedative — Prescription Only (S4)." },
  { id: "enrofloxacin", name: "Enrofloxacin injection", active: "Enrofloxacin 50 mg/mL", strength: "100 mL vial", form: "vial", img: IMG.vial,
    schedule: "S4", note: "Fluoroquinolone antibiotic — Prescription Only (S4)." },
];

// Pick a randomised subset for a run. Guarantees at least one S4 and one S8 so
// the locked cabinet and S8 safe are always exercised; the rest is random and
// the final display order is fully shuffled (not sorted by schedule).
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function pickDrugs(n = 6) {
  const pool = shuffle(SCHEDULED_DRUGS);
  const must = [];
  for (const sch of ["S8", "S4"]) {
    const d = pool.find((x) => x.schedule === sch && !must.includes(x));
    if (d) must.push(d);
  }
  const rest = pool.filter((d) => !must.includes(d));
  return shuffle([...must, ...rest].slice(0, n));
}

// ── Phase 3 · S8 drug register reconciliation scenario ────────────────────────
export const REGISTER_SCENARIO = {
  drug: "Methadone injection 10 mg/mL (10 mL vial)",
  unit: "mg",
  opening: 100,
  events: [
    { id: "e1", time: "10:15", desc: "Administered to Bella (induction)", out: 20 },
    { id: "e2", time: "13:40", desc: "Administered to Max (top-up)", out: 15 },
    { id: "e3", time: "14:05", desc: "Wastage — part-vial discarded (witnessed)", out: 5 },
  ],
  // opening 100 − (20 + 15 + 5) = 60 mg expected
  get expectedClosing() {
    return this.opening - this.events.reduce((s, e) => s + e.out, 0);
  },
  physicalCount: 55, // physical stock is 5 mg short of the book — a real discrepancy
  actions: [
    { id: "report", label: "Report the discrepancy to the vet/authorised person and investigate (document it)", correct: true },
    { id: "adjust", label: "Quietly adjust the register figure to match the 55 mg physical count", correct: false },
    { id: "ignore", label: "Ignore it — a 5 mg difference is within normal tolerance", correct: false },
  ],
  // multi-select compliance safeguards (some correct, some not)
  safeguards: [
    { id: "immediate", label: "Record each S8 transaction in the register immediately", correct: true },
    { id: "witness", label: "A second authorised person witnesses and co-signs each administration/wastage", correct: true },
    { id: "running", label: "Keep a running balance and reconcile it against physical stock", correct: true },
    { id: "safe", label: "Return the vial to the locked S8 safe between uses", correct: true },
    { id: "weekly", label: "Batch up entries and write them into the register at the end of the week", correct: false },
    { id: "round", label: "Round the running balance to the nearest 10 mg to save time", correct: false },
  ],
};

export const DRUGSTORAGE_READING = {
  blurb: "Scheduling, storage and controlled-drug record-keeping references at veterinary-nurse standard.",
  links: [
    { label: "Poisons Standard (SUSMP) — current edition", source: "TGA", href: "https://www.tga.gov.au/products/regulations-all-products/legislation-and-legislative-instruments/poisons-standard-susmp" },
    { label: "Scheduling of medicines and poisons — overview", source: "healthdirect", href: "https://www.healthdirect.gov.au/scheduling-of-medicines-and-poisons" },
    { label: "Storage & recording of Schedule 8 drugs of addiction", source: "NSW Health", href: "https://www.health.nsw.gov.au/pharmaceutical/Pages/drugs-of-addiction-sch8.aspx" },
    { label: "Drugs and poisons — clinic obligations", source: "Vic Health", href: "https://www.health.vic.gov.au/drugs-and-poisons/poisons-code" },
  ],
};

// ── Challenge mode · "spot the breach" timed dispensary inspection ─────────────
// Each station can render a compliant (ok) or non-compliant (breach) state. A run
// randomly activates 3–4 breaches; the learner taps the ones they spot before time runs out.
export const INSPECT_SECONDS = 50;

export const INSPECTION_STATIONS = [
  { id: "cabinet", type: "cabinet", label: "S4 medicine cabinet", x: 4, y: 6,
    breachDetail: "The S4 cabinet was left open — prescription medicines must be kept where clients/public cannot access them.",
    okDetail: "S4 cabinet closed and locked." },
  { id: "stock", type: "stock", label: "Shelf stock", x: 37, y: 6,
    breachDetail: "Expired stock on the shelf — out-of-date medicines must be pulled and quarantined for disposal.",
    okDetail: "Stock is in date." },
  { id: "keys", type: "keys", label: "Drug-safe keys", x: 70, y: 6,
    breachDetail: "Controlled-drug keys were left in the open key box — keys must be secured with restricted access.",
    okDetail: "Keys secured." },
  { id: "safe", type: "safe", label: "S8 controlled-drug safe", x: 4, y: 54,
    breachDetail: "The S8 safe was left unlocked — controlled drugs must be in a locked, fixed safe at all times.",
    okDetail: "S8 safe locked and secure." },
  { id: "bench", type: "bench", label: "Preparation bench", x: 37, y: 54,
    breachDetail: "An S8 vial and drawn-up syringe were left unattended on the bench — controlled drugs must never be left out.",
    okDetail: "Bench is clear." },
  { id: "register", type: "register", label: "S8 register", x: 70, y: 54,
    breachDetail: "The S8 register has a missing/unsigned entry — every transaction must be recorded and co-signed immediately.",
    okDetail: "Register complete and signed." },
];

export function buildInspection() {
  const ids = INSPECTION_STATIONS.map((s) => s.id);
  const n = 3 + Math.floor(Math.random() * 2); // 3 or 4 breaches
  const shuffled = [...ids].sort(() => Math.random() - 0.5);
  const breachIds = new Set(shuffled.slice(0, n));
  return INSPECTION_STATIONS.map((s) => ({ ...s, breach: breachIds.has(s.id) }));
}

